import {
  BufferedDataLayerEvent,
  DataLayerEventName,
  DataLayerObject,
  DataLayerOptional,
  DataLayerWindow,
} from './types'
import localConfig from '~/config/locale-config'

declare let window: DataLayerWindow

// Paths whose page view is still to come; see expectPageView. Keyed by path, so a client-side return to a path before
// its first page view fires shares that view's events. Rare while browse links reload the page; key by visit under CSR.
const pendingPageViews = new Set<string>()

/**
 * Holds the events of the page at `path` until its own page view, which can arrive after the shopper has
 * navigated on. Returns a function that gives up the hold if the page view never fires.
 */
export const expectPageView = (path: string) => {
  pendingPageViews.add(path)
  return () => pendingPageViews.delete(path)
}

const isPageView = (obj: DataLayerObject | DataLayerOptional): obj is DataLayerObject =>
  'event' in obj && obj.event === 'custom.content.view'

// A page view releases its own page's events, and those of pages the shopper has left that won't get one.
const releaseBuffered = (pageViewPath: string | undefined) => {
  const currentPath = window.location.pathname
  const held: BufferedDataLayerEvent[] = []
  for (const buffered of window.dataLayerBuffer) {
    const isReleased =
      buffered.path === pageViewPath || (buffered.path !== currentPath && !pendingPageViews.has(buffered.path))
    if (isReleased) window.dataLayer.push(buffered.event)
    else held.push(buffered)
  }
  window.dataLayerBuffer = held
}

export const pushToDataLayer = (obj: DataLayerObject | DataLayerOptional): void => {
  try {
    window.dataLayerBuffer = window.dataLayerBuffer || []
    window.dataLayer = window.dataLayer || []
    const path = window.location.pathname

    if (isPageView(obj)) {
      window.dataLayer.push(obj)
      if (obj.route?.currentPath) pendingPageViews.delete(obj.route.currentPath)
      releaseBuffered(obj.route?.currentPath)
      return
    }

    const hasPageView = window.dataLayer.some((dl: any) => isPageView(dl) && dl.route && path === dl.route.currentPath)
    if (hasPageView) {
      window.dataLayer.push(obj)
    } else {
      window.dataLayerBuffer.push({ path, event: obj })
    }
  } catch (error) {
    console.error('Error in pushing to datalayer:', error)
  }
}

const makeDatalayerLanguageData = (locale: Locales) => {
  return {
    language: localConfig?.[locale]?.language ?? 'Unknown',
    languageISO: localConfig?.[locale]?.iso ?? 'Unknown',
  }
}

const currentCampaign = () =>
  import.meta.client && tryUseNuxtApp() ? useCampaignAttribution().campaign.value : undefined

export const pushEventToDataLayer = (
  eventName: DataLayerEventName,
  options: DataLayerOptional,
  locale?: Locales,
): void => {
  const payload: DataLayerObject = {
    event: eventName,
    ...options,
  }
  const brazeCampaign = currentCampaign()
  if (brazeCampaign) payload.brazeCampaign = brazeCampaign
  if (eventName === 'custom.content.view') {
    payload.content = {
      type: payload.content?.type ?? '',
      ...makeDatalayerLanguageData(locale ?? 'en-AU'),
      ...payload.content,
    }
  }
  pushToDataLayer(payload)
}
